import { Inbox, Loader2, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-5 w-full', className)} aria-hidden="true" />;
}

/** Light blue bars on 55px rows, the same shape the portal shows while loading. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex h-[55px] items-center gap-6 border-b border-[color:var(--ts-row-border)] px-4 last:border-b-0"
        >
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton key={columnIndex} className="flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * A card whose content has not arrived: its heading, a line of description, and a body.
 *
 * Tables had this and cards did not, so a screen part-way through loading showed finished
 * headings above empty space, which reads as "there is nothing here" rather than "this is
 * still coming".
 */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card p-6" aria-hidden="true">
      <Skeleton className="h-6 w-[220px]" />
      <Skeleton className="mt-3 h-4 w-[340px] max-w-full" />
      <div className="mt-6 flex flex-col gap-3">
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton key={index} className="h-4" />
        ))}
      </div>
    </div>
  );
}

/** The row of figures at the head of an e-file, before the figures are known. */
export function StatsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="flex flex-wrap gap-8" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex min-w-[120px] flex-col gap-2">
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-7 w-[60px]" />
        </div>
      ))}
    </div>
  );
}

/** A tab strip whose tabs are not known yet. */
export function TabsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex flex-wrap items-center gap-2 pb-5" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-9 w-[110px] rounded-full" />
      ))}
    </div>
  );
}


/**
 * A timeline waiting for its events: the mark, the line joining them, the label and time.
 *
 * Matched to FilingTimeline - a 28px mark, the connector under it, two lines of text - so a
 * card holding a timeline does not draw table rows while it waits and then rearrange itself.
 */
export function TimelineSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <ol className="flex flex-col" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <li key={index} className="flex gap-3">
          <div className="flex flex-col items-center">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            {index < rows - 1 ? (
              <span className="w-px flex-1 bg-[color:var(--ts-border-soft)]" aria-hidden="true" />
            ) : null}
          </div>
          <div className="flex flex-1 flex-col gap-2 pb-6">
            <Skeleton className="h-4 w-[180px]" />
            <Skeleton className="h-3 w-[120px]" />
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * A card that holds a form: fields in pairs, each a label over a control.
 */
export function FormSkeleton({ fields = 6 }: { fields?: number }) {
  return (
    <div className="card p-6" aria-hidden="true">
      <Skeleton className="h-6 w-[200px]" />
      <Skeleton className="mt-3 h-4 w-[320px] max-w-full" />
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {Array.from({ length: fields }).map((_, index) => (
          <div key={index} className="flex flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[90px]" />
            <Skeleton className="h-10 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A block of label-over-value facts, as the summary and the details panel lay them out. */
function FactsSkeleton({ count, columns = 3 }: { count: number; columns?: 1 | 3 }) {
  return (
    <div
      className={cn('grid gap-x-6 gap-y-5', columns === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3')}
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-[70px]" />
          <Skeleton className="h-5 w-[110px]" />
        </div>
      ))}
    </div>
  );
}

/**
 * The e-file page: its summary and figures, its transmissions and records down the left, its
 * activity and details down the right.
 *
 * The generic page skeleton drew a search box over a table for every route, including this
 * one, so the whole page rearranged itself the moment the e-file arrived. This is that
 * page's own shape, down to the 320px second column.
 */
export function DetailSkeleton() {
  return (
    <div className="grid items-start gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_320px]" aria-hidden="true">
      <div className="flex min-w-0 flex-col gap-6">
        <div className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 pb-5">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-[140px]" />
              <Skeleton className="h-4 w-[280px] max-w-full" />
            </div>
            <Skeleton className="h-10 w-[150px] rounded-md" />
          </div>
          <FactsSkeleton count={6} />
          <div className="mt-6 border-t border-[color:var(--ts-row-border)] pt-5">
            <StatsSkeleton count={4} />
          </div>
        </div>

        <div className="card p-6">
          <Skeleton className="h-6 w-[170px]" />
          <Skeleton className="mt-3 h-4 w-[300px] max-w-full" />
          <div className="mt-5">
            <TableSkeleton rows={2} columns={5} />
          </div>
        </div>

        <div className="card p-6">
          <Skeleton className="h-6 w-[120px]" />
          <Skeleton className="mt-3 h-4 w-[260px] max-w-full" />
          <div className="mt-5">
            <TableSkeleton rows={8} columns={6} />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="card p-6">
          <Skeleton className="h-6 w-[100px]" />
          <div className="mt-5">
            <TimelineSkeleton rows={4} />
          </div>
        </div>
        <div className="card p-6">
          <Skeleton className="h-6 w-[90px]" />
          <div className="mt-5">
            <FactsSkeleton count={4} columns={1} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A listing page: the toolbar the portal puts above its tables, then the table.
 */
export function ListSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="card p-6" aria-hidden="true">
      <div className="flex flex-wrap items-end gap-3 pb-5">
        <div className="flex w-full max-w-[380px] min-w-[200px] flex-col gap-1.5">
          <Skeleton className="h-3.5 w-[60px]" />
          <Skeleton className="h-10 rounded-md" />
        </div>
        <div className="ml-auto flex items-end gap-3">
          <Skeleton className="h-10 w-[140px] rounded-md" />
          <Skeleton className="h-10 w-[120px] rounded-md" />
        </div>
      </div>
      <TableSkeleton rows={rows} columns={columns} />
    </div>
  );
}

/**
 * The skeleton that belongs to a given address.
 *
 * Every route names its own pending shape, but the shell - drawn while the API says who is
 * signed in, which is what a reload goes through - had no route to ask and drew a search box
 * over a table whatever page was being reloaded. It reads the address instead, because the
 * address already says which page is coming.
 */
export function RouteSkeleton({ pathname }: { pathname: string }) {
  if (/^\/e-files\/[^/]+\/(correct|replace)$/.test(pathname)) return <FormSkeleton fields={4} />;
  if (pathname.startsWith('/e-files/new')) return <FormSkeleton fields={6} />;
  if (/^\/e-files\/[^/]+$/.test(pathname)) return <DetailSkeleton />;
  if (pathname.startsWith('/e-files')) return <ListSkeleton rows={8} columns={7} />;
  if (pathname.startsWith('/settings')) return <ListSkeleton rows={5} columns={5} />;
  if (pathname.startsWith('/developers')) return <CardSkeleton lines={6} />;
  if (/^\/admin\/customers\/[^/]+$/.test(pathname)) return <DetailSkeleton />;
  if (pathname.startsWith('/admin/usage')) return <ListSkeleton rows={6} columns={5} />;
  if (pathname.startsWith('/admin/staff')) return <ListSkeleton rows={6} columns={4} />;
  if (pathname.startsWith('/admin')) return <ListSkeleton rows={8} columns={5} />;
  return <ListSkeleton />;
}

/**
 * The application, before it knows who is looking at it.
 *
 * This used to be three bars breathing in the middle of an empty page. The portal never does
 * that: its chrome is drawn immediately and only the parts that depend on an answer are
 * skeletons, so the window looks like the product from the first frame and nothing moves
 * when the answer arrives.
 *
 * The measurements are the shell's own - a 263px menu, its 74px logo row, a 64px header -
 * so what replaces this lands exactly where the skeleton was.
 */
export function AppShellSkeleton({ pathname }: { pathname?: string }) {
  const where = pathname ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  return (
    <div className="flex min-h-screen bg-surface" aria-busy="true" aria-label="Loading">
      <nav
        className="hidden w-[263px] shrink-0 flex-col border-r border-[color:var(--ts-border-soft)] bg-surface lg:flex"
        aria-hidden="true"
      >
        <div className="flex h-[74px] shrink-0 items-center px-5">
          <Skeleton className="h-9 w-[128px] rounded-lg" />
        </div>
        <div className="flex flex-col gap-1.5 px-4 pt-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex h-10 items-center gap-3 px-2">
              <Skeleton className="h-5 w-5 shrink-0 rounded-md" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
        <div className="mt-auto flex items-center gap-3 border-t border-[color:var(--ts-border-soft)] p-4">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-[120px]" />
            <Skeleton className="h-3 w-[80px]" />
          </div>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-4 border-b border-[color:var(--ts-border-soft)] bg-surface px-6"
          aria-hidden="true"
        >
          <Skeleton className="h-4 w-[180px]" />
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <Skeleton className="h-4 w-[140px]" />
            <Skeleton className="h-9 w-9 rounded-full" />
          </div>
        </header>
        <main className="flex-1 bg-[image:linear-gradient(to_bottom,var(--ts-ground-top),var(--ts-ground-bottom))]">
          <div className="flex w-full flex-col gap-6 p-6">
            <RouteSkeleton pathname={where} />
          </div>
        </main>
      </div>
    </div>
  );
}

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-muted-foreground">
      <Loader2 className={cn('h-4 w-4 motion-safe:animate-spin', className)} aria-hidden="true" />
      {label ? <span className="text-sm">{label}</span> : null}
    </span>
  );
}

/** A light blue circle, one sentence and one button. Nothing else. */
export function EmptyState({
  title,
  description,
  icon: Icon = Inbox,
  action,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--ts-primary-light)]">
        <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <div>
        <p className="text-lg font-semibold text-gray-3">{title}</p>
        {description ? <p className="mt-1 max-w-md text-base text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function ErrorNote({
  title,
  detail,
  action,
  onDismiss,
}: {
  title: string;
  detail?: string;
  action?: React.ReactNode;
  /** When given, the note shows a close control. Errors never hide on their own. */
  onDismiss?: () => void;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'relative flex flex-col items-start gap-2 rounded-lg border border-[color:var(--ts-pill-danger-border)]',
        'bg-[color:var(--ts-pill-danger-bg)] px-4 py-3',
        onDismiss ? 'pr-11' : null,
      )}
    >
      <p className="text-base font-medium text-destructive">{title}</p>
      {detail ? <p className="text-base text-gray-3">{detail}</p> : null}
      {action}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full text-destructive transition-colors duration-150 hover:bg-[color:var(--ts-pill-danger-border)]"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
